import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {
  Alert, Button, Collapse, Empty, Modal, Popconfirm, Switch, Table, Tag,
  Tooltip as Hint, Typography,
} from 'antd';
import {
  CalendarOutlined, DeleteOutlined, EditOutlined, ExpandAltOutlined, LeftOutlined,
  PlusOutlined, QuestionCircleOutlined, RightOutlined,
} from '@ant-design/icons';
import {
  Area, CartesianGrid, ComposedChart, Customized, Legend, Line, ReferenceLine,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import * as fmt from '../format';
import { lineLitres, skuCatalog } from '../events';
import {
  useCreateBrewsBulk, useDeleteBrew, useDeleteEvent, useUpdateBrew,
} from '../hooks';
import { AuTransitInput, STOCK_BUCKETS } from '../PlanTab';
import {
  applyDrafts, buildArrivals, clampWeekDelta, clampWeekIndex, defaultAddDraft,
  draftsToRequests, projectSkuWeeks, projectWeeks, recommendationId, tankYMax,
} from './arrivals';
import TankBars from './TankBars';

const { Text } = Typography;

const COLORS = {
  plan: '#1677ff',
  max: '#52c41a',
  existing: '#95de64',
  recommended: '#ffa940',
  actual: '#262626',
  forecast: '#1677ff',
  stockout: '#cf1322',
  event: '#722ed1',
};

const PACK_COLORS = [
  '#1677ff', '#13c2c2', '#722ed1', '#eb2f96', '#fa8c16', '#52c41a', '#2f54eb', '#a0d911',
];

const COVER_BAND_FILL = 'forecasting-cover-band';
const Y_AXIS_WIDTH = 44;
const AXIS_PADDING = { left: 0, right: 0 };
const VOLUME_MARGIN = {
  top: 18, right: 8, bottom: 0, left: 0,
};
const TANKS_MARGIN = {
  top: 20, right: 8, bottom: 0, left: 0,
};
const CHART_MARGIN = {
  top: 4, right: 0, bottom: 0, left: 0,
};
const HISTORY_AXIS_WIDTH = 32;
const TICK = { fontSize: 11 };
const legendStyle = { fontSize: 11, lineHeight: '16px', paddingTop: 4 };
const WEEK_SYNC = 'beer-weeks';

// Week-chart legend removed from the header. Restore WEEK_LEGEND plus the swatches
// in the chart head to bring it back.
// const WEEK_LEGEND = [
//   { color: COLORS.plan, label: 'Projected stock' },
//   { color: COLORS.max, label: 'Target range' },
//   { color: COLORS.stockout, label: 'SKU out', line: true },
//   { color: COLORS.existing, label: 'Tanks / planned' },
//   { color: COLORS.recommended, label: 'Recommended' },
// ];

const atLeastZero = (v) => (v == null ? v : Math.max(0, v));
const packOpening = (weeks) => {
  const first = (weeks || [])[0] || {};
  return Math.max(
    0,
    (first.stockEndPlanLitres || 0) + (first.demandLitres || 0)
      - (first.existingArrivalsLitres || 0) - (first.recommendedArrivalsLitres || 0),
  );
};
const axisLitres = (v) => `${Math.round(v / 1000)}k`;
const tooltipLitres = (value) => (Array.isArray(value)
  ? `${fmt.litres(value[0])} – ${fmt.litres(value[1])}`
  : fmt.litres(value));
const tanksTick = (v) => (v ? axisLitres(v) : '');

const eventWhen = (event) => (
  event.startDate === event.endDate
    ? fmt.shortDate(event.startDate)
    : `${fmt.shortDate(event.startDate)} – ${fmt.shortDate(event.endDate)}`
);

const eventLineText = (line, catalog) => {
  const litres = lineLitres(line, catalog);
  if (line.unit === 'litres' || litres == null) {
    return line.unit === 'units'
      ? `${fmt.number(line.quantity)} units`
      : fmt.litres(line.quantity);
  }
  return `${fmt.number(line.quantity)} units (${fmt.litres(litres)})`;
};

const expiryRiskHint = (line) => {
  const batches = (line.expiryBatches || [])
    .map((b) => `${b.batch || 'No batch'}: ${fmt.number(b.unitsAtRisk)} by `
      + `${fmt.shortDate(b.expiryDate)}`)
    .join(' · ');
  return `Forecast won't sell these before best-before, oldest batch first. ${batches}`;
};

// Week i spans x = i - 0.5 .. i + 0.5 on both weekly charts.
const MONTH_LINE_STROKE = '#8c8c8c';

/** x positions of the end of each calendar month inside the plotted weeks. */
const monthEndPositions = (weeks) => weeks.flatMap((w, i) => {
  const start = dayjs(w.weekStart);
  const dayOfWeek = start.endOf('month').diff(start, 'day');
  return dayOfWeek < 7 ? [i - 0.5 + (dayOfWeek + 1) / 7] : [];
});

/** recharts only renders ReferenceLine elements that are direct chart children. */
const monthLines = (positions) => positions.map((x) => (
  <ReferenceLine key={`month-${x}`} x={x} stroke={MONTH_LINE_STROKE} ifOverflow="hidden" />
));

const TipRow = ({ label, children }) => (
  <div className="forecasting-week-tooltip-row">
    <span>{label}</span>
    <span>{children}</span>
  </div>
);

TipRow.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const WeekTooltip = ({ active, payload, packs }) => {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  if (row.isStart) return null;
  const total = packs
    ? packs.reduce((sum, pack) => sum + (row[pack.key] || 0), 0)
    : row.stockEndPlanLitres;
  return (
    <div className="forecasting-week-tooltip">
      <div className="forecasting-week-tooltip-title">{`Week of ${row.label}`}</div>
      {packs ? packs.map((pack) => (
        <TipRow key={pack.key} label={pack.label}>{fmt.litres(row[pack.key])}</TipRow>
      )) : (
        <TipRow label="Projected stock"><strong>{fmt.litres(row.stockEndPlanLitres)}</strong></TipRow>
      )}
      {packs && <TipRow label="Total"><strong>{fmt.litres(total)}</strong></TipRow>}
      <TipRow label="Target range">
        {`${fmt.litres(row.coverBand[0])} – ${fmt.litres(row.coverBand[1])}`}
      </TipRow>
      <TipRow label="Demand">{fmt.litres(row.demandLitres)}</TipRow>
      {!!row.eventDemandLitres && (
        <TipRow label="Events">{fmt.litres(row.eventDemandLitres)}</TipRow>
      )}
      {row.existingArrivalsLitres > 0 && (
        <TipRow label="Tanks + planned">{fmt.litres(row.existingArrivalsLitres)}</TipRow>
      )}
      {row.recommendedArrivalsLitres > 0 && (
        <TipRow label="Recommended">{fmt.litres(row.recommendedArrivalsLitres)}</TipRow>
      )}
    </div>
  );
};

WeekTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.object),
  packs: PropTypes.arrayOf(PropTypes.object),
};

WeekTooltip.defaultProps = {
  active: false,
  payload: [],
  packs: null,
};

const eventMark = ({ cx, cy, payload }) => {
  if (payload == null || payload.eventDot == null || cx == null || cy == null) return null;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={3.5}
      fill="#fff"
      stroke={COLORS.event}
      strokeWidth={2}
    />
  );
};

eventMark.propTypes = {
  cx: PropTypes.number,
  cy: PropTypes.number,
  payload: PropTypes.shape({ eventDot: PropTypes.number }),
};

eventMark.defaultProps = {
  cx: null,
  cy: null,
  payload: null,
};

/** One red line per stockout week. Nearby weeks stack their labels so SKU names stay readable. */
const placeStockouts = (stockouts, weeks) => {
  const byWeek = new Map();
  (stockouts || []).forEach((mark) => {
    const x = weeks.findIndex((week) => week.weekStart === mark.week);
    if (x < 0 || !mark.label) return;
    const labels = byWeek.get(x) || [];
    if (!labels.includes(mark.label)) labels.push(mark.label);
    byWeek.set(x, labels);
  });
  let lane = 0;
  let lastX = -99;
  return [...byWeek.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([x, labels]) => {
      if (x - lastX > 2) lane = 0;
      else lane += 1;
      lastX = x;
      return {
        x, label: labels.join(', '), lane,
      };
    });
};

const WeeklyChart = ({
  weeks, stockouts, arrivals, yMax, canEdit, lossPct, onDraftChange, onRemove,
  onDragActive, showTanks, packs,
}) => {
  const packFields = (index) => {
    if (!packs) return {};
    const fields = {};
    packs.forEach((pack) => {
      fields[pack.key] = index < 0 ? pack.opening : (pack.values[index] || 0);
    });
    return fields;
  };
  const rows = weeks.map((w, i) => {
    const fields = packFields(i);
    const stock = packs
      ? Object.values(fields).reduce((sum, value) => sum + value, 0)
      : atLeastZero(w.stockEndPlanLitres);
    return {
      x: i,
      xLine: i,
      label: fmt.shortDate(w.weekStart),
      existingArrivalsLitres: w.existingArrivalsLitres,
      recommendedArrivalsLitres: w.recommendedArrivalsLitres,
      stockEndPlanLitres: atLeastZero(w.stockEndPlanLitres),
      demandLitres: atLeastZero(w.demandLitres),
      eventDemandLitres: w.eventDemandLitres || 0,
      eventDot: w.eventDemandLitres ? stock : null,
      coverBand: [atLeastZero(w.minLevelLitres), atLeastZero(w.maxLevelLitres)],
      ...fields,
    };
  });
  // Opening stock at the left edge, so the line spans the full axis like the bars do.
  const first = weeks[0] || {};
  const data = rows.length ? [{
    isStart: true,
    x: -1,
    xLine: -0.5,
    stockEndPlanLitres: atLeastZero((first.stockEndPlanLitres || 0) + (first.demandLitres || 0)
      - (first.existingArrivalsLitres || 0) - (first.recommendedArrivalsLitres || 0)),
    coverBand: rows[0].coverBand,
    ...packFields(-1),
  }, ...rows] : rows;
  const domain = [-0.5, rows.length - 0.5];
  const ticks = rows.filter((r) => r.x % 2 === 0).map((r) => r.x);
  const tickLabel = (x) => (rows[x] ? rows[x].label : '');
  const stockoutRows = placeStockouts(stockouts, weeks);
  const stockoutLane = stockoutRows.reduce((max, row) => Math.max(max, row.lane), 0);
  const volumeMargin = {
    ...VOLUME_MARGIN,
    top: VOLUME_MARGIN.top + stockoutLane * 13,
  };
  const monthEnds = monthEndPositions(weeks);
  const volumeClass = showTanks
    ? 'forecasting-chart-volume'
    : 'forecasting-chart-volume forecasting-chart-volume-solo';
  return (
    <>
      <div className={volumeClass}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={volumeMargin} syncId={WEEK_SYNC}>
            <defs>
              <linearGradient id={COVER_BAND_FILL} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={COLORS.max} stopOpacity={0.28} />
                <stop offset="100%" stopColor={COLORS.max} stopOpacity={0.06} />
              </linearGradient>
            </defs>
            <XAxis
              type="number"
              dataKey="xLine"
              domain={domain}
              allowDataOverflow
              height={showTanks ? 0 : undefined}
              hide={showTanks}
              ticks={showTanks ? undefined : ticks}
              interval={0}
              tickFormatter={tickLabel}
              tick={TICK}
              padding={AXIS_PADDING}
            />
            <YAxis
              width={Y_AXIS_WIDTH}
              domain={[0, 'auto']}
              tickFormatter={axisLitres}
              tick={TICK}
            />
            <Tooltip content={<WeekTooltip packs={packs} />} cursor={{ stroke: '#d9d9d9' }} />
            <Area
              type="monotone"
              dataKey="coverBand"
              fill={`url(#${COVER_BAND_FILL})`}
              stroke="none"
              activeDot={false}
              isAnimationActive={false}
            />
            {packs ? packs.map((pack) => (
              <Area
                key={pack.key}
                type="linear"
                dataKey={pack.key}
                name={pack.label}
                stackId="packs"
                stroke={pack.color}
                strokeWidth={1}
                fill={pack.color}
                fillOpacity={0.72}
                activeDot={false}
                isAnimationActive={false}
              />
            )) : (
              <Area
                type="monotone"
                dataKey="stockEndPlanLitres"
                stroke={COLORS.plan}
                strokeWidth={2}
                fill={COLORS.plan}
                fillOpacity={0.12}
                activeDot={false}
                isAnimationActive={false}
              />
            )}
            <Line
              dataKey="eventDot"
              stroke="none"
              strokeWidth={0}
              dot={eventMark}
              activeDot={false}
              connectNulls={false}
              isAnimationActive={false}
              legendType="none"
            />
            {monthLines(monthEnds)}
            {stockoutRows.map((row) => (
              <ReferenceLine
                key={`out-${row.x}`}
                x={row.x}
                stroke={COLORS.stockout}
                strokeWidth={2}
                label={{
                  value: row.label,
                  position: 'top',
                  fill: COLORS.stockout,
                  fontSize: 11,
                  offset: 6 + row.lane * 13,
                }}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {showTanks && (
      <div className="forecasting-chart-tanks">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={TANKS_MARGIN} syncId={WEEK_SYNC}>
            {monthLines(monthEnds)}
            <XAxis
              type="number"
              dataKey="x"
              domain={domain}
              allowDataOverflow
              ticks={ticks}
              interval={0}
              tickFormatter={tickLabel}
              tick={TICK}
              padding={AXIS_PADDING}
            />
            <YAxis
              width={Y_AXIS_WIDTH}
              domain={[0, yMax]}
              allowDataOverflow
              tickCount={3}
              tickFormatter={tanksTick}
              tick={TICK}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={() => null} cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }} />
            <Customized
              component={(
                <TankBars
                  arrivals={arrivals}
                  weekStarts={weeks.map((w) => w.weekStart)}
                  canEdit={canEdit}
                  lossPct={lossPct}
                  onDraftChange={onDraftChange}
                  onRemove={onRemove}
                  onDragActive={onDragActive}
                />
              )}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      )}
    </>
  );
};

WeeklyChart.propTypes = {
  weeks: PropTypes.arrayOf(PropTypes.object).isRequired,
  stockouts: PropTypes.arrayOf(PropTypes.shape({
    week: PropTypes.string,
    label: PropTypes.string,
  })),
  arrivals: PropTypes.arrayOf(PropTypes.object).isRequired,
  yMax: PropTypes.number.isRequired,
  canEdit: PropTypes.bool.isRequired,
  lossPct: PropTypes.number.isRequired,
  onDraftChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onDragActive: PropTypes.func.isRequired,
  showTanks: PropTypes.bool,
  packs: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    label: PropTypes.string,
    color: PropTypes.string,
    opening: PropTypes.number,
    values: PropTypes.arrayOf(PropTypes.number),
  })),
};

WeeklyChart.defaultProps = {
  stockouts: [],
  showTanks: true,
  packs: null,
};

const MonthlyChart = ({ actual, forecast }) => {
  const data = useMemo(() => {
    const byMonth = {};
    actual.forEach((m) => {
      byMonth[m.month] = { month: m.month, actual: atLeastZero(m.litresNet) };
    });
    forecast.forEach((m) => {
      byMonth[m.month] = {
        ...(byMonth[m.month] || { month: m.month }),
        forecast: atLeastZero(m.forecastLitres),
        band: m.lowerLitres != null
          ? [atLeastZero(m.lowerLitres), atLeastZero(m.upperLitres)]
          : undefined,
      };
    });
    return Object.values(byMonth)
      .sort((a, b) => a.month.localeCompare(b.month))
      .map((m) => ({ ...m, label: fmt.month(m.month) }));
  }, [actual, forecast]);
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} margin={CHART_MARGIN}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="label" tick={TICK} />
        <YAxis
          width={HISTORY_AXIS_WIDTH}
          domain={[0, 'auto']}
          tickFormatter={axisLitres}
          tick={TICK}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip formatter={tooltipLitres} />
        <Legend iconSize={8} wrapperStyle={legendStyle} />
        <Line
          type="monotone"
          dataKey="actual"
          name="History"
          stroke={COLORS.actual}
          dot={false}
          isAnimationActive={false}
        />
        <Line
          type="monotone"
          dataKey="forecast"
          name="Forecast"
          stroke={COLORS.forecast}
          strokeWidth={2}
          dot={false}
          isAnimationActive={false}
        />
        <Area
          dataKey="band"
          name="80% range"
          fill={COLORS.forecast}
          fillOpacity={0.12}
          stroke="none"
          isAnimationActive={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

MonthlyChart.propTypes = {
  actual: PropTypes.arrayOf(PropTypes.object).isRequired,
  forecast: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const SnapGroup = ({ title, extra, children }) => (
  <section className="forecasting-snap-group">
    <div className="forecasting-snap-head">
      <span className="forecasting-snap-title">{title}</span>
      {extra}
    </div>
    {children}
  </section>
);

SnapGroup.propTypes = {
  title: PropTypes.string.isRequired,
  extra: PropTypes.node,
  children: PropTypes.node.isRequired,
};

SnapGroup.defaultProps = { extra: null };

const SnapRow = ({ label, hint, children }) => (
  <div className="forecasting-snap-row">
    <Text type="secondary">
      {label}
      {hint && (
        <Hint title={hint}>
          <QuestionCircleOutlined className="forecasting-snap-hint" />
        </Hint>
      )}
    </Text>
    <span className="forecasting-snap-value">{children}</span>
  </div>
);

SnapRow.propTypes = {
  label: PropTypes.string.isRequired,
  hint: PropTypes.string,
  children: PropTypes.node.isRequired,
};

SnapRow.defaultProps = { hint: null };

const skuKey = (line) => `${line.sku}-${line.viaMix || ''}`;

const skuLabel = (line) => (line.viaMix ? `${line.sku} in ${line.viaMix}` : line.sku);

// Used by the commented Pipeline and demand block. Restore with ArrowUpOutlined
// and ArrowDownOutlined imports.
// const RUN_OUT_HINT = 'Counts stock, tanks and planned brews. Packs can\'t be swapped.';

const SkuSparkline = ({ weeks, runOutWeek }) => {
  const width = 220;
  const height = 36;
  const series = weeks || [];
  if (!series.length) return null;
  const stock = series.map((week) => Math.max(0, week.stockEndPlanLitres || 0));
  const upper = series.map((week) => Math.max(0, week.maxLevelLitres || 0));
  const lower = series.map((week) => Math.max(0, week.minLevelLitres || 0));
  const peak = Math.max(1, ...stock, ...upper);
  const xAt = (index) => (index / Math.max(series.length - 1, 1)) * width;
  const yAt = (value) => height - 1 - ((value / peak) * (height - 2));
  const point = (value, index) => `${xAt(index).toFixed(1)} ${yAt(value).toFixed(1)}`;
  const line = stock.map((value, index) => `${index === 0 ? 'M' : 'L'}${point(value, index)}`).join(' ');
  const band = [
    ...upper.map((value, index) => `${index === 0 ? 'M' : 'L'}${point(value, index)}`),
    ...lower.slice().reverse().map((value, index) => (
      `L${point(value, series.length - 1 - index)}`
    )),
    'Z',
  ].join(' ');
  const runOutIdx = runOutWeek
    ? series.findIndex((week) => week.weekStart === runOutWeek)
    : -1;
  const runOutX = runOutIdx >= 0 ? xAt(runOutIdx).toFixed(1) : null;
  return (
    <svg
      className="forecasting-sku-spark"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path d={band} fill={COLORS.max} fillOpacity="0.28" />
      <path
        d={line}
        fill="none"
        stroke={COLORS.plan}
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
      />
      {runOutX && (
        <line
          x1={runOutX}
          x2={runOutX}
          y1="0"
          y2={height}
          stroke={COLORS.stockout}
          strokeWidth="1.5"
          vectorEffect="non-scaling-stroke"
        />
      )}
    </svg>
  );
};

SkuSparkline.propTypes = {
  weeks: PropTypes.arrayOf(PropTypes.object),
  runOutWeek: PropTypes.string,
};

SkuSparkline.defaultProps = { weeks: [], runOutWeek: null };

const coverTone = (weeks, min, max) => {
  if (weeks == null) return 'none';
  if (min != null && weeks < min) return 'low';
  if (max != null && weeks > max) return 'high';
  return 'ok';
};

const shortCover = (n) => (n < 10 ? n.toFixed(1) : String(Math.round(n)));

const coverCellText = (weeks, asMonths) => {
  if (weeks == null) return '–';
  if (weeks >= 52) return asMonths ? '12+' : '52+';
  return shortCover(asMonths ? weeks / fmt.WEEKS_PER_MONTH : weeks);
};

const coverLimit = (weeks, asMonths) => {
  if (weeks == null) return '–';
  return asMonths ? shortCover(weeks / fmt.WEEKS_PER_MONTH) : String(weeks);
};

const coverTip = (weeks, min, max, asMonths) => {
  const target = `${coverLimit(min, asMonths)}–${coverLimit(max, asMonths)} `
    + `${asMonths ? 'mo' : 'wk'} target`;
  if (weeks == null) return `No forecast · ${target}`;
  const cover = asMonths ? fmt.months(weeks) : fmt.weeks(weeks);
  return `${cover} · ${target}`;
};

const Snapshot = ({
  beer, skuLines, canEdit, focusedSkuKey, onFocusSku,
}) => {
  const [showAllSkus, setShowAllSkus] = useState(false);
  const [asMonths, setAsMonths] = useState(false);
  const buckets = beer.stockUnitsByBucket || {};
  const lines = skuLines || beer.skuLines;
  const stockedSkus = lines.filter((l) => l.stockUnits > 0);
  const hiddenSkus = lines.length - stockedSkus.length;
  const skuRows = showAllSkus ? lines : stockedSkus;
  const { rules } = beer;

  return (
    <div className="forecasting-snapshot">
      {/* Saleable cover hero. Cover now sits on each SKU graph as a coloured cell.
      <div className="forecasting-cover-hero">
        <div>
          <Text type="secondary">
            Saleable cover
            <Hint title="NZ, AUS and AU transit stock against forward forecast demand.">
              <QuestionCircleOutlined className="forecasting-snap-hint" />
            </Hint>
          </Text>
          <div className="forecasting-cover-value">{fmt.weeks(beer.coverWeeks)}</div>
          <Text type="secondary">
            {`${fmt.litres(beer.stockLitres)} · ${fmt.number(beer.skuCount)} `
              + `${beer.skuCount === 1 ? 'SKU' : 'SKUs'}`}
          </Text>
        </div>
        <div className="forecasting-cover-side">
          <SnapRow
            label="All stock incl. WIP / AUS / export"
            hint="Every location bucket, including WIP and export, divided by average weekly demand."
          >
            {fmt.weeks(beer.stockVolumeWeeks)}
          </SnapRow>
          <SnapRow
            label="Incl. tanks + planned"
            hint="Saleable stock plus beer in tanks and planned brews, against forward demand."
          >
            {fmt.weeks(beer.coverWithPipelineWeeks)}
          </SnapRow>
          <SnapRow label="Target">{`${rules.minCoverWeeks} – ${rules.maxCoverWeeks} wk`}</SnapRow>
        </div>
      </div>
      */}

      <div className="forecasting-snap-grid forecasting-cover-stock">
        {STOCK_BUCKETS.map(([key, label]) => (
          <SnapRow key={key} label={label}>
            {key === 'auTransit' && canEdit
              ? <AuTransitInput beer={beer} />
              : fmt.number(buckets[key] || 0)}
          </SnapRow>
        ))}
      </div>

      {/* Pipeline and demand
      <SnapGroup title="Pipeline and demand">
        <SnapRow label="In tanks">{fmt.litres(beer.tankLitres)}</SnapRow>
        <SnapRow label="Planned, not yet brewed">{fmt.litres(beer.plannedLitres)}</SnapRow>
        <SnapRow label="Forecast demand / wk">
          {fmt.litres(beer.avgWeeklyDemandLitres)}
          {beer.demandDirection === 'up' && (
            <ArrowUpOutlined className="forecasting-demand-up forecasting-snap-icon" />
          )}
          {beer.demandDirection === 'down' && (
            <ArrowDownOutlined className="forecasting-demand-down forecasting-snap-icon" />
          )}
        </SnapRow>
        {beer.recentWeeklyDemandLitres != null && (
          <SnapRow label="Last 4 weeks / wk">{fmt.litres(beer.recentWeeklyDemandLitres)}</SnapRow>
        )}
        <SnapRow label="First pack out" hint={RUN_OUT_HINT}>
          {beer.firstPackOut ? (
            <span className="forecasting-snap-stack">
              <Text type="danger" strong>
                {`${skuLabel(beer.firstPackOut)} · week of ${
                  fmt.shortDate(beer.firstPackOut.week)}`}
              </Text>
              <Text type="secondary">
                {`${Math.round((beer.firstPackOut.demandShare || 0) * 100)}% of demand`}
              </Text>
            </span>
          ) : 'Not in horizon'}
        </SnapRow>
        <SnapRow label="All packs gone" hint={RUN_OUT_HINT}>
          {beer.allPacksOutWeek
            ? `Week of ${fmt.shortDate(beer.allPacksOutWeek)}`
            : 'Not in horizon'}
        </SnapRow>
      </SnapGroup>
      */}

      {/* Rules
      <SnapGroup
        title="Rules"
        extra={rules.overridden.length > 0 ? <Tag>beer override</Tag> : null}
      >
        <div className="forecasting-snap-grid">
          <SnapRow label="Min cover">{`${rules.minCoverWeeks} wk`}</SnapRow>
          <SnapRow label="Max cover">{`${rules.maxCoverWeeks} wk`}</SnapRow>
          <SnapRow label="Lead time">{`${rules.leadTimeDays} days`}</SnapRow>
          <SnapRow label="Brewery">{rules.preferredBrewery || 'Any'}</SnapRow>
        </div>
      </SnapGroup>
      */}

      <div className="forecasting-sku-toolbar">
        <label className="forecasting-snap-toggle" htmlFor="forecasting-cover-months">
          <Text type="secondary">{asMonths ? 'Months' : 'Weeks'}</Text>
          <Switch
            id="forecasting-cover-months"
            size="small"
            checked={asMonths}
            onChange={setAsMonths}
            aria-label="Show cover in months"
          />
        </label>
        {hiddenSkus > 0 && (
          <label className="forecasting-snap-toggle" htmlFor="forecasting-show-all-skus">
            <Text type="secondary">{`Show all (${hiddenSkus} with no stock)`}</Text>
            <Switch
              id="forecasting-show-all-skus"
              size="small"
              checked={showAllSkus}
              onChange={setShowAllSkus}
            />
          </label>
        )}
      </div>
      {skuRows.length ? (
        <div className="forecasting-sku-table">
          <div className="forecasting-sku-metrics forecasting-sku-head">
            <span>SKU</span>
            <span>Stock</span>
            <span>Expiry</span>
          </div>
          {skuRows.map((line) => {
            const key = skuKey(line);
            const focused = key === focusedSkuKey;
            const runOut = line.stockoutWeek;
            const blockClass = focused
              ? 'forecasting-sku-block forecasting-sku-focused'
              : 'forecasting-sku-block';
            return (
              <div key={key} className={blockClass}>
                <div className="forecasting-sku-metrics">
                  <div>
                    <div>{line.sku}</div>
                    <Text type="secondary">{line.viaMix ? `in ${line.viaMix}` : line.unit}</Text>
                  </div>
                  <div>
                    <div>{fmt.number(line.stockUnits)}</div>
                    <Text type="secondary">{fmt.litres(line.stockLitres)}</Text>
                  </div>
                  <div>
                    <div>{fmt.shortDate(line.earliestExpiry)}</div>
                    {line.expiredUnits > 0 && (
                      <Text type="danger">{`${fmt.number(line.expiredUnits)} expired`}</Text>
                    )}
                    {line.unitsAtRisk > 0 && (
                      <Hint title={expiryRiskHint(line)}>
                        <div>
                          <Text type="warning">{`${fmt.number(line.unitsAtRisk)} at risk`}</Text>
                        </div>
                      </Hint>
                    )}
                  </div>
                </div>
                <div className="forecasting-sku-spark-row">
                  <span className="forecasting-sku-cover-wrap">
                    <Hint
                      title={coverTip(
                        line.coverWeeks,
                        rules.minCoverWeeks,
                        rules.maxCoverWeeks,
                        asMonths,
                      )}
                    >
                      <span
                        className={`forecasting-sku-cover is-${coverTone(
                          line.coverWeeks,
                          rules.minCoverWeeks,
                          rules.maxCoverWeeks,
                        )}`}
                      >
                        {coverCellText(line.coverWeeks, asMonths)}
                      </span>
                    </Hint>
                    {runOut && (
                      <span className="forecasting-sku-runout">{fmt.shortDate(runOut)}</span>
                    )}
                  </span>
                  <SkuSparkline weeks={line.weeks} runOutWeek={runOut} />
                  <Button
                    size="small"
                    type="text"
                    icon={<ExpandAltOutlined />}
                    aria-label={`Show ${skuLabel(line)} projected stock`}
                    onClick={() => onFocusSku(key)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      ) : <Text type="secondary">No SKUs with stock</Text>}

      <div className="forecasting-history">
        <SnapGroup title="History & Forecast">
          <div className="forecasting-history-chart">
            <MonthlyChart
              actual={beer.actualMonthly || []}
              forecast={beer.monthly || []}
            />
          </div>
        </SnapGroup>
      </div>
    </div>
  );
};

Snapshot.propTypes = {
  beer: PropTypes.shape({
    stockUnitsByBucket: PropTypes.shape({}),
    skuLines: PropTypes.arrayOf(PropTypes.object),
    skuCount: PropTypes.number,
    stockLitres: PropTypes.number,
    stockVolumeWeeks: PropTypes.number,
    coverWeeks: PropTypes.number,
    coverWithPipelineWeeks: PropTypes.number,
    firstPackOut: PropTypes.shape({
      week: PropTypes.string,
      sku: PropTypes.string,
      viaMix: PropTypes.string,
      demandShare: PropTypes.number,
    }),
    allPacksOutWeek: PropTypes.string,
    tankLitres: PropTypes.number,
    plannedLitres: PropTypes.number,
    avgWeeklyDemandLitres: PropTypes.number,
    recentWeeklyDemandLitres: PropTypes.number,
    demandDirection: PropTypes.string,
    stockoutWeek: PropTypes.string,
    actualMonthly: PropTypes.arrayOf(PropTypes.object),
    monthly: PropTypes.arrayOf(PropTypes.object),
    rules: PropTypes.shape({
      minCoverWeeks: PropTypes.number,
      maxCoverWeeks: PropTypes.number,
      leadTimeDays: PropTypes.number,
      preferredBrewery: PropTypes.string,
      overridden: PropTypes.arrayOf(PropTypes.string),
    }),
  }).isRequired,
  skuLines: PropTypes.arrayOf(PropTypes.object).isRequired,
  canEdit: PropTypes.bool.isRequired,
  focusedSkuKey: PropTypes.string,
  onFocusSku: PropTypes.func.isRequired,
};

Snapshot.defaultProps = { focusedSkuKey: null };

const RecCard = ({
  rec, canEdit, onSchedule, onRestore, plannedDraft, removedDraft,
}) => (
  <div className={removedDraft ? 'forecasting-rec-card forecasting-rec-removed' : 'forecasting-rec-card'}>
    <div className="forecasting-rec-summary">
      <span className="forecasting-rec-status">
        <Tag color={fmt.URGENCY[rec.urgency].color}>{fmt.URGENCY[rec.urgency].label}</Tag>
        {plannedDraft && <Tag color="blue">Planned</Tag>}
        {removedDraft && <Tag>Removed</Tag>}
      </span>
      <span className="forecasting-rec-date">{fmt.date(rec.brewDate)}</span>
      <span className="forecasting-rec-size">{fmt.litres(rec.tankLitres)}</span>
    </div>
    <Collapse ghost className="forecasting-rec-details">
      <Collapse.Panel header="Details" key="details">
        <div>{rec.brewery}</div>
        <Text type="secondary">
          {`Ready ${fmt.shortDate(rec.arrivalDate)} · cover ${fmt.weeks(rec.coverBeforeWeeks)} → `
            + `${fmt.weeks(rec.coverAfterWeeks)}`}
        </Text>
        <div className="forecasting-rec-split">
          {rec.packSplit.map((p) => (
            <span key={`${p.sku}-${p.viaMix || ''}`}>{fmt.packLine(p)}</span>
          ))}
        </div>
        {rec.notes.map((note) => (
          <div key={note}><Text type="warning">{note}</Text></div>
        ))}
        {canEdit && !removedDraft && (
          <Button size="small" icon={<CalendarOutlined />} onClick={() => onSchedule(rec)}>
            Add to schedule
          </Button>
        )}
        {canEdit && removedDraft && (
          <Button size="small" onClick={onRestore}>Undo</Button>
        )}
      </Collapse.Panel>
    </Collapse>
    {/* Previous always-open card
    <div className="forecasting-rec-top">
      <span>
        <Tag color={fmt.URGENCY[rec.urgency].color}>{fmt.URGENCY[rec.urgency].label}</Tag>
        {plannedDraft && <Tag color="blue">Planned (unsaved)</Tag>}
        {removedDraft && <Tag>Removed (unsaved)</Tag>}
      </span>
      {canEdit && !removedDraft && (
        <Button size="small" icon={<CalendarOutlined />} onClick={() => onSchedule(rec)}>
          Add to schedule
        </Button>
      )}
      {canEdit && removedDraft && (
        <Button size="small" onClick={onRestore}>Undo</Button>
      )}
    </div>
    <div className="forecasting-rec-title">{fmt.date(rec.brewDate)}</div>
    <div>{`${fmt.litres(rec.tankLitres)} · ${rec.brewery}`}</div>
    <Text type="secondary">
      {`Ready ${fmt.shortDate(rec.arrivalDate)} · cover ${fmt.weeks(rec.coverBeforeWeeks)} → `
        + `${fmt.weeks(rec.coverAfterWeeks)}`}
    </Text>
    <div className="forecasting-rec-split">
      {rec.packSplit.map((p) => (
        <span key={`${p.sku}-${p.viaMix || ''}`}>{fmt.packLine(p)}</span>
      ))}
    </div>
    {rec.notes.map((note) => (
      <div key={note}><Text type="warning">{note}</Text></div>
    ))}
    */}
  </div>
);

RecCard.propTypes = {
  rec: PropTypes.shape({
    urgency: PropTypes.string,
    tankLitres: PropTypes.number,
    brewery: PropTypes.string,
    brewDate: PropTypes.string,
    arrivalDate: PropTypes.string,
    coverBeforeWeeks: PropTypes.number,
    coverAfterWeeks: PropTypes.number,
    packSplit: PropTypes.arrayOf(PropTypes.object),
    notes: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  canEdit: PropTypes.bool.isRequired,
  onSchedule: PropTypes.func.isRequired,
  onRestore: PropTypes.func,
  plannedDraft: PropTypes.bool,
  removedDraft: PropTypes.bool,
};

RecCard.defaultProps = {
  onRestore: () => {},
  plannedDraft: false,
  removedDraft: false,
};

const tankColumns = [
  { title: 'Gyle', dataIndex: 'name' },
  { title: 'Location', dataIndex: 'locations' },
  {
    title: 'In tank', dataIndex: 'availableLitres', align: 'right', render: fmt.litres,
  },
  { title: 'Brewed', dataIndex: 'brewDate', render: fmt.shortDate },
  {
    title: 'Ready (est.)',
    dataIndex: 'eta',
    render: (eta, g) => (
      <>
        {fmt.shortDate(eta)}
        {g.etaEstimated && <Tag className="forecasting-inline-tag">overdue, assumed soon</Tag>}
      </>
    ),
  },
];

const scheduleColumns = (canEdit, onEditBrew, isRemoved, onRemove, onRestore) => [
  { title: 'Brew date', dataIndex: 'brewDate', render: fmt.shortDate },
  { title: 'Brewery', dataIndex: 'brewery' },
  {
    title: 'Volume', dataIndex: 'volumeLitres', align: 'right', render: fmt.litres,
  },
  {
    title: 'Status',
    dataIndex: 'status',
    render: (status, b) => (
      <>
        <Tag color={fmt.BREW_STATUS[status]?.color}>{fmt.BREW_STATUS[status]?.label}</Tag>
        {!b.counted && <Tag>{`counted as tank ${b.linkedGyle}`}</Tag>}
        {b.overdue && <Tag color="red">brew date passed</Tag>}
        {isRemoved(`brew:${b.id}`) && <Tag>Removed (unsaved)</Tag>}
      </>
    ),
  },
  { title: 'Ready', dataIndex: 'eta', render: fmt.shortDate },
  {
    title: '',
    align: 'right',
    render: (_, b) => {
      if (!canEdit) return null;
      const id = `brew:${b.id}`;
      if (isRemoved(id)) {
        return <Button size="small" onClick={() => onRestore(id)}>Undo</Button>;
      }
      return (
        <>
          <Button
            size="small"
            icon={<EditOutlined />}
            aria-label="Edit brew"
            onClick={() => onEditBrew(b)}
          />
          {b.counted && b.status === 'planned' && (
            <Button
              size="small"
              icon={<DeleteOutlined />}
              aria-label="Remove brew"
              onClick={() => onRemove(id)}
            />
          )}
        </>
      );
    },
  },
];

const eventColumns = (canEdit, onOpenEvent, onDelete) => [
  { title: 'When', dataIndex: 'when' },
  {
    title: 'Event',
    dataIndex: 'name',
    render: (name, ev) => (ev.notes ? <span title={ev.notes}>{name}</span> : name),
  },
  { title: 'SKUs', dataIndex: 'contents' },
  {
    title: '',
    align: 'right',
    render: (_, ev) => {
      if (!canEdit) return null;
      return (
        <>
          <Button
            size="small"
            icon={<EditOutlined />}
            aria-label={`Edit ${ev.name}`}
            onClick={() => onOpenEvent(ev.full || ev)}
          />
          <Popconfirm title="Remove this event?" onConfirm={() => onDelete(ev.id)}>
            <Button
              size="small"
              icon={<DeleteOutlined />}
              aria-label={`Remove ${ev.name}`}
            />
          </Popconfirm>
        </>
      );
    },
  },
];

const typingTarget = (target) => {
  const tag = target && target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
};

const BeerDrawer = ({
  beer, order, plan, canEdit, onClose, onStep, onScheduleRecommendation, onEditBrew,
  onOpenEvent,
}) => {
  const [recsOpen, setRecsOpen] = useState(false);
  const [byPack, setByPack] = useState(false);
  const [allSkuOut, setAllSkuOut] = useState(false);
  const [focusedSkuKey, setFocusedSkuKey] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [lockedYMax, setLockedYMax] = useState(null);
  const createBulk = useCreateBrewsBulk();
  const deleteEvent = useDeleteEvent();
  const updateBrew = useUpdateBrew();
  const deleteBrew = useDeleteBrew();
  const addSeq = useRef(0);
  const visible = !!beer;
  const index = beer ? order.indexOf(beer.beerName) : -1;
  const atStart = index <= 0;
  const atEnd = index < 0 || index >= order.length - 1;
  const beerName = beer ? beer.beerName : null;
  const arrivals = useMemo(
    () => (beer ? buildArrivals(beer, plan) : []),
    [beer, plan],
  );
  const adjusted = useMemo(
    () => applyDrafts(arrivals, drafts, plan),
    [arrivals, drafts, plan],
  );
  const chartArrivals = useMemo(
    () => adjusted.filter((item) => item.kind !== 'rec'),
    [adjusted],
  );
  const projectedWeeks = useMemo(
    () => (beer ? projectWeeks(beer.weeks, arrivals, chartArrivals) : []),
    [beer, arrivals, chartArrivals],
  );
  const projectedSkuLines = useMemo(
    () => (beer
      ? projectSkuWeeks(
        beer.skuLines,
        arrivals,
        chartArrivals,
        beer.rules && beer.rules.maxCoverWeeks,
      )
      : []),
    [beer, arrivals, chartArrivals],
  );
  const focusedLine = projectedSkuLines.find((line) => skuKey(line) === focusedSkuKey) || null;
  const panelEvents = useMemo(() => {
    if (!beer) return [];
    const catalog = skuCatalog(plan);
    const skus = new Set((beer.skuLines || []).map((line) => line.sku));
    return (beer.events || []).map((ev) => {
      const full = (plan.events || []).find((row) => String(row.id) === String(ev.id)) || null;
      const lines = ((full && full.lines) || [])
        .filter((line) => skus.has(line.sku))
        .map((line, lineIndex) => ({
          key: `${line.sku}-${line.unit}-${lineIndex}`,
          sku: line.sku,
          qty: eventLineText(line, catalog),
          litres: lineLitres(line, catalog),
        }));
      const entered = lines.reduce((sum, line) => sum + (line.litres || 0), 0);
      const skuText = lines.length
        ? lines.map((line) => `${line.sku} ${line.qty}`).join(', ')
        : fmt.litres(ev.litres);
      const counted = lines.length && Math.abs(entered - ev.litres) > 1
        ? `${fmt.litres(ev.litres)} on this beer`
        : null;
      return {
        ...ev,
        full,
        when: full ? eventWhen(full) : '—',
        contents: counted ? `${skuText} · ${counted}` : skuText,
        notes: (full && full.notes) || null,
      };
    });
  }, [beer, plan]);
  const showByPack = byPack && !focusedLine;
  const packSeries = useMemo(() => {
    if (!showByPack) return null;
    return projectedSkuLines.map((line, i) => ({
      key: `pack${i}`,
      label: skuLabel(line),
      color: PACK_COLORS[i % PACK_COLORS.length],
      opening: packOpening(line.weeks),
      values: (line.weeks || []).map((week) => Math.max(0, week.stockEndPlanLitres || 0)),
    }));
  }, [showByPack, projectedSkuLines]);
  const weekTitle = focusedLine
    ? `Weekly Planning · ${skuLabel(focusedLine)}`
    : 'Weekly Planning';
  /*
  let legendItems = WEEK_LEGEND;
  if (focusedLine) {
    legendItems = WEEK_LEGEND
      .filter((item) => item.label !== 'Tanks / planned' && item.label !== 'Recommended');
  } else if (packSeries && packSeries.length) {
    legendItems = [
      ...packSeries.map((pack) => ({ key: pack.key, color: pack.color, label: pack.label })),
      { color: COLORS.max, label: 'Target range' },
      { color: COLORS.stockout, label: 'SKU out', line: true },
      { color: COLORS.existing, label: 'Tanks / planned' },
      { color: COLORS.recommended, label: 'Recommended' },
    ];
  }
  const shownWeeks = focusedLine ? (focusedLine.weeks || []) : projectedWeeks;
  if (shownWeeks.some((week) => week.eventDemandLitres)) {
    legendItems = [...legendItems, { color: COLORS.event, label: 'Event', dot: true }];
  }
  if (!chartArrivals.some((item) => item.kind === 'rec')) {
    legendItems = legendItems.filter((item) => item.label !== 'Recommended');
  }
  */
  const stockouts = useMemo(() => {
    if (!beer) return [];
    if (focusedLine) {
      return focusedLine.stockoutWeek
        ? [{ week: focusedLine.stockoutWeek, label: skuLabel(focusedLine) }]
        : [];
    }
    if (!allSkuOut) {
      return beer.firstPackOut
        ? [{ week: beer.firstPackOut.week, label: skuLabel(beer.firstPackOut) }]
        : [];
    }
    return projectedSkuLines
      .filter((line) => line.stockoutWeek)
      .map((line) => ({ week: line.stockoutWeek, label: skuLabel(line) }))
      .sort((a, b) => a.week.localeCompare(b.week) || a.label.localeCompare(b.label));
  }, [beer, focusedLine, allSkuOut, projectedSkuLines]);
  const computedYMax = useMemo(
    () => tankYMax(chartArrivals, plan),
    [chartArrivals, plan],
  );
  const yMax = lockedYMax == null ? computedYMax : lockedYMax;
  const lossPct = (plan.settings && plan.settings.packagingLossPct) || 0;
  const dirtyCount = Object.keys(drafts).length;
  const recArrivals = arrivals.filter((item) => item.kind === 'rec');
  const scheduleSettled = !recArrivals.length || recArrivals.every((item) => {
    const draft = drafts[item.id];
    if (!draft) return false;
    return !!(draft.removed || draft.accepted
      || draft.weekIdx != null || draft.volumeLitres != null);
  });
  const unsavedSchedule = adjusted.filter((item) => item.added || item.draftFromRec);
  const requests = useMemo(
    () => draftsToRequests(arrivals, adjusted),
    [arrivals, adjusted],
  );
  const canPersist = requests.creates.length + requests.updates.length + requests.deletes.length > 0;
  const saving = createBulk.isLoading || updateBrew.isLoading || deleteBrew.isLoading;

  const arrivalsRef = useRef(arrivals);
  const planRef = useRef(plan);
  const draftsRef = useRef(drafts);
  const yMaxRef = useRef(computedYMax);
  const confirmingRef = useRef(false);
  arrivalsRef.current = arrivals;
  planRef.current = plan;
  draftsRef.current = drafts;
  yMaxRef.current = computedYMax;

  const onDraftChange = useCallback((id, draft) => {
    const currentPlan = planRef.current;
    if (!currentPlan) return;
    const volumeLitres = Math.round(Number(draft.volumeLitres));
    if (!Number.isFinite(volumeLitres) || volumeLitres <= 0) return;
    setDrafts((prev) => {
      const existing = prev[id];
      if (existing && existing.removed) return prev;
      if (existing && existing.added) {
        const weekIdx = clampWeekIndex(draft.weekIdx, currentPlan);
        if (existing.weekIdx === weekIdx && existing.volumeLitres === volumeLitres) return prev;
        return {
          ...prev,
          [id]: { ...existing, weekIdx, volumeLitres },
        };
      }
      const item = arrivalsRef.current.find((row) => row.id === id);
      if (!item || !item.editable) return prev;
      const weekIdx = item.weekIdx + clampWeekDelta(
        item,
        draft.weekIdx - item.weekIdx,
        currentPlan,
      );
      const current = prev[id];
      const accepted = !!(current && current.accepted);
      if (weekIdx === item.weekIdx && volumeLitres === item.volumeLitres) {
        if (accepted) {
          if (current.weekIdx === weekIdx && current.volumeLitres === volumeLitres) return prev;
          return {
            ...prev,
            [id]: {
              ...current,
              weekIdx,
              volumeLitres,
              accepted: true,
            },
          };
        }
        if (!prev[id]) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      }
      if (current && current.weekIdx === weekIdx && current.volumeLitres === volumeLitres) {
        return prev;
      }
      return {
        ...prev,
        [id]: {
          weekIdx,
          volumeLitres,
          ...(accepted ? { accepted: true } : {}),
        },
      };
    });
  }, []);

  const onRemove = useCallback((id) => {
    setDrafts((prev) => {
      const existing = prev[id];
      if (existing && existing.added) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      const item = arrivalsRef.current.find((row) => row.id === id);
      if (!item || !item.editable) return prev;
      if (existing && existing.removed) return prev;
      return { ...prev, [id]: { removed: true } };
    });
  }, []);

  const onRestore = useCallback((id) => {
    setDrafts((prev) => {
      if (!prev[id] || prev[id].added) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const onAddBrew = () => {
    if (!beer) return;
    addSeq.current += 1;
    const id = `new:${addSeq.current}`;
    setDrafts((prev) => ({ ...prev, [id]: defaultAddDraft(beer, plan) }));
  };

  const onDraftSchedule = () => {
    setDrafts((prev) => {
      const next = { ...prev };
      arrivalsRef.current.forEach((item) => {
        if (item.kind !== 'rec') return;
        const existing = next[item.id];
        if (existing && (existing.removed || existing.accepted)) return;
        if (existing && (existing.weekIdx != null || existing.volumeLitres != null)) return;
        next[item.id] = {
          accepted: true,
          weekIdx: item.weekIdx,
          volumeLitres: item.volumeLitres,
        };
      });
      return next;
    });
  };

  const onDragActive = useCallback((active) => {
    setLockedYMax(active ? yMaxRef.current : null);
  }, []);

  const discardThen = useCallback((action) => {
    if (!Object.keys(draftsRef.current).length) {
      action();
      return;
    }
    if (confirmingRef.current) return;
    confirmingRef.current = true;
    Modal.confirm({
      title: 'Discard tank changes?',
      content: 'Unsaved tank edits on this chart will be lost.',
      okText: 'Discard',
      okButtonProps: { danger: true },
      onOk: () => {
        confirmingRef.current = false;
        setDrafts({});
        action();
      },
      onCancel: () => {
        confirmingRef.current = false;
      },
    });
  }, []);

  const saveDrafts = async () => {
    const { creates, updates, deletes } = draftsToRequests(arrivalsRef.current, adjusted);
    if (!creates.length && !updates.length && !deletes.length) return;
    try {
      await deletes.reduce(
        (chain, id) => chain.then(() => deleteBrew.mutateAsync(id)),
        Promise.resolve(),
      );
      if (creates.length) await createBulk.mutateAsync(creates);
      await updates.reduce(
        (chain, update) => chain.then(() => updateBrew.mutateAsync(update)),
        Promise.resolve(),
      );
      setDrafts({});
      setLockedYMax(null);
    } catch (err) {
      // The forecasting API helpers already toast the failure. Keep the draft for a retry.
      if (err) setDrafts((prev) => prev);
    }
  };

  useEffect(() => {
    setDrafts({});
    setLockedYMax(null);
    setFocusedSkuKey(null);
  }, [beerName]);

  useEffect(() => {
    if (!visible) return undefined;
    const onKey = (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (typingTarget(e.target)) return;
      const wrap = e.target.closest && e.target.closest('.ant-modal-wrap');
      if (wrap && !wrap.querySelector('.forecasting-beer-modal')) return;
      e.preventDefault();
      discardThen(() => onStep(e.key === 'ArrowLeft' ? -1 : 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, onStep, discardThen]);

  return (
    <Modal
      className="forecasting-beer-modal"
      width="calc(100vw - 24px)"
      style={{ top: 12, paddingBottom: 0 }}
      visible={visible}
      onCancel={() => discardThen(onClose)}
      footer={null}
      destroyOnClose
      title={beer && (
        <div className="forecasting-beer-title">
          <Button
            icon={<LeftOutlined />}
            disabled={atStart}
            aria-label="Previous beer"
            onClick={() => discardThen(() => onStep(-1))}
          />
          <div className="forecasting-beer-title-text">
            <span className="forecasting-beer-title-name">{beer.beerName}</span>
            <Tag color={fmt.STATUS[beer.status].color}>{fmt.STATUS[beer.status].label}</Tag>
            {beer.method && beer.method !== 'arima_ensemble' && (
              <Tag color={fmt.METHOD[beer.method]?.color}>{fmt.METHOD[beer.method]?.label}</Tag>
            )}
            {index >= 0 && (
              <Text type="secondary">{`${index + 1} of ${order.length}`}</Text>
            )}
          </div>
          <Button
            icon={<RightOutlined />}
            disabled={atEnd}
            aria-label="Next beer"
            onClick={() => discardThen(() => onStep(1))}
          />
        </div>
      )}
    >
      {beer && (
        <div className={`forecasting-beer-body${recsOpen ? '' : ' forecasting-recs-collapsed'}`}>
          <div className="forecasting-pane forecasting-pane-scroll">
            <Snapshot
              key={beer.beerName}
              beer={beer}
              skuLines={projectedSkuLines}
              canEdit={canEdit}
              focusedSkuKey={focusedLine ? focusedSkuKey : null}
              onFocusSku={setFocusedSkuKey}
            />
          </div>

          <div className="forecasting-beer-centre">
            <div className="forecasting-pane forecasting-chart-pane">
              <div className="forecasting-pane-head forecasting-chart-head">
                <span className="forecasting-pane-title">{weekTitle}</span>
                {focusedLine && (
                  <Button size="small" onClick={() => setFocusedSkuKey(null)}>
                    Beer chart
                  </Button>
                )}
                {/* Week chart legend and Week / Month toggle. Month chart now sits in
                    the left column as History & Forecast.
                {chartMode === 'week' ? weekTitle : 'Forecast by month'}
                <div className="forecasting-chart-legend">
                  {legendItems.map((item) => (
                    <span key={item.key || item.label} className="forecasting-chart-legend-item">
                      <span
                        className={[
                          'forecasting-chart-swatch',
                          item.line ? 'forecasting-chart-swatch-line' : '',
                          item.dot ? 'forecasting-chart-swatch-dot' : '',
                        ].filter(Boolean).join(' ')}
                        style={item.dot
                          ? { borderColor: item.color }
                          : { background: item.color }}
                      />
                      {item.label}
                    </span>
                  ))}
                </div>
                <Radio.Group
                  size="small"
                  value={chartMode}
                  onChange={(e) => {
                    const next = e.target.value;
                    setChartMode(next);
                    if (next === 'month') setFocusedSkuKey(null);
                  }}
                >
                  <Radio.Button value="week">Week</Radio.Button>
                  <Radio.Button value="month">Month</Radio.Button>
                </Radio.Group>
                */}
                {!focusedLine && (
                  <span className="forecasting-snap-toggle">
                    <Text type="secondary">All SKUs out</Text>
                    <Switch
                      size="small"
                      checked={allSkuOut}
                      onChange={setAllSkuOut}
                      aria-label="All SKUs out"
                    />
                  </span>
                )}
                {!focusedLine && (
                  <span className="forecasting-snap-toggle">
                    <Text type="secondary">By pack</Text>
                    <Switch
                      size="small"
                      checked={byPack}
                      onChange={setByPack}
                      aria-label="By pack"
                    />
                  </span>
                )}
              </div>
              {/* Drag hint
              {canEdit && !focusedLine && (
                <div className="forecasting-drag-hint">
                  <span>
                    Drag a bar to move its week. Drag the top edge to resize in 1,000 L steps.
                    × removes it.
                  </span>
                </div>
              )}
              */}
              {dirtyCount > 0 && (
                <div className="forecasting-draft-bar">
                  <span className="forecasting-draft-note">
                    {canPersist
                      ? `${dirtyCount} unsaved ${dirtyCount === 1 ? 'change' : 'changes'}`
                        + ' · other recommendations update after save'
                      : 'Removed recommendations are a what-if. Discard to bring them back.'}
                  </span>
                  <Button size="small" onClick={() => setDrafts({})} disabled={saving}>
                    Discard
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    onClick={saveDrafts}
                    loading={saving}
                    disabled={!canPersist}
                  >
                    Save
                  </Button>
                </div>
              )}
              <div className="forecasting-chart">
                <WeeklyChart
                  key={focusedLine
                    ? `${beer.beerName}-${skuKey(focusedLine)}`
                    : `${beer.beerName}-${showByPack ? 'pack' : 'pool'}`}
                  weeks={focusedLine ? (focusedLine.weeks || []) : projectedWeeks}
                  stockouts={stockouts}
                  arrivals={chartArrivals}
                  yMax={yMax}
                  canEdit={canEdit && !saving && !focusedLine}
                  lossPct={lossPct}
                  onDraftChange={onDraftChange}
                  onRemove={onRemove}
                  onDragActive={onDragActive}
                  showTanks={!focusedLine}
                  packs={packSeries && packSeries.length ? packSeries : null}
                />
                {/* Month view moved to History & Forecast in the left column.
                <div className="forecasting-chart-volume">
                  <MonthlyChart
                    key={beer.beerName}
                    actual={beer.actualMonthly}
                    forecast={beer.monthly}
                  />
                </div>
                */}
              </div>
            </div>

            <div className="forecasting-pane forecasting-pane-scroll forecasting-pipeline-pane">
              <section>
                <div className="forecasting-pane-head">
                  <span className="forecasting-pane-title">In tanks</span>
                </div>
                {beer.tankGyles.length ? (
                  <Table
                    size="small"
                    rowKey="sku"
                    pagination={false}
                    dataSource={beer.tankGyles}
                    columns={tankColumns}
                  />
                ) : <Text type="secondary">No gyles in tank</Text>}
              </section>
              <section>
                <div className="forecasting-pane-head">
                  <span className="forecasting-pane-title">Production schedule</span>
                  {canEdit && !focusedLine && (
                    <Button
                      size="small"
                      type="link"
                      icon={<PlusOutlined />}
                      onClick={onAddBrew}
                      disabled={saving}
                    >
                      Add brew
                    </Button>
                  )}
                </div>
                {unsavedSchedule.map((item) => (
                  <div key={item.id} className="forecasting-draft-brew">
                    <Tag color="blue">Unsaved</Tag>
                    <span>
                      {`${fmt.litres(item.volumeLitres)} · ready ${fmt.shortDate(
                        item.source?.packageDate || item.source?.arrivalDate,
                      )}`}
                    </span>
                    <Button size="small" type="link" onClick={() => onRemove(item.id)}>
                      Remove
                    </Button>
                  </div>
                ))}
                {beer.plannedBrews.length ? (
                  <Table
                    size="small"
                    rowKey="id"
                    pagination={false}
                    dataSource={beer.plannedBrews}
                    columns={scheduleColumns(
                      canEdit,
                      onEditBrew,
                      (id) => !!(drafts[id] && drafts[id].removed),
                      onRemove,
                      onRestore,
                    )}
                  />
                ) : !unsavedSchedule.length && (
                  <Text type="secondary">Nothing planned for this beer</Text>
                )}
              </section>
              <section>
                <div className="forecasting-pane-head">
                  <span className="forecasting-pane-title">Events</span>
                  {canEdit && (
                    <Button
                      size="small"
                      type="link"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        const line = (beer.skuLines || []).find((l) => !l.viaMix)
                          || (beer.skuLines || [])[0];
                        onOpenEvent(line ? { lines: [{ sku: line.sku, unit: 'litres' }] } : null);
                      }}
                    >
                      Add event
                    </Button>
                  )}
                </div>
                {panelEvents.length ? (
                  <Table
                    size="small"
                    rowKey="id"
                    pagination={false}
                    dataSource={panelEvents}
                    columns={eventColumns(canEdit, onOpenEvent, (id) => deleteEvent.mutate(id))}
                  />
                ) : <Text type="secondary">No demand events for this beer</Text>}
              </section>
            </div>
          </div>

          <div className={recsOpen
            ? 'forecasting-pane forecasting-pane-scroll forecasting-recs-pane'
            : 'forecasting-pane forecasting-recs-pane forecasting-recs-closed'}
          >
            {recsOpen ? (
              <>
                <div className="forecasting-pane-head">
                  <span className="forecasting-pane-title">Recommended brews</span>
                  <span className="forecasting-rec-actions">
                    {beer.recommendations.length > 0 && (
                      <Text type="secondary">{beer.recommendations.length}</Text>
                    )}
                    {canEdit && beer.recommendations.length > 0 && (
                      <Button
                        size="small"
                        onClick={onDraftSchedule}
                        disabled={scheduleSettled || saving}
                      >
                        Draft schedule
                      </Button>
                    )}
                    <Button
                      size="small"
                      icon={<RightOutlined />}
                      aria-label="Hide recommended brews"
                      onClick={() => setRecsOpen(false)}
                    />
                  </span>
                </div>
                {beer.warnings.map((w) => (
                  <Alert
                    key={w}
                    className="forecasting-rec-alert"
                    type="warning"
                    showIcon
                    message={w}
                  />
                ))}
                {beer.recommendations.length ? beer.recommendations.map((rec, recIndex) => {
                  const recKey = recommendationId(rec, recIndex);
                  const recDraft = drafts[recKey];
                  return (
                    <RecCard
                      key={recKey}
                      rec={rec}
                      canEdit={canEdit}
                      plannedDraft={!!(recDraft && !recDraft.removed && !recDraft.added)}
                      removedDraft={!!(recDraft && recDraft.removed)}
                      onRestore={() => onRestore(recKey)}
                      onSchedule={onScheduleRecommendation}
                    />
                  );
                }) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={`No brew needed in the next ${plan.weekStarts.length} weeks`}
                  />
                )}
              </>
            ) : (
              <button
                type="button"
                className="forecasting-recs-rail"
                onClick={() => setRecsOpen(true)}
              >
                <span className="forecasting-recs-rail-label">Recommended brews</span>
                <span>{beer.recommendations.length}</span>
                {beer.warnings.length > 0 && <span className="forecasting-recs-warn" />}
              </button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

BeerDrawer.propTypes = {
  beer: PropTypes.shape({
    beerName: PropTypes.string,
    status: PropTypes.string,
    method: PropTypes.string,
    warnings: PropTypes.arrayOf(PropTypes.string),
    stockoutWeek: PropTypes.string,
    weeks: PropTypes.arrayOf(PropTypes.object),
    recommendations: PropTypes.arrayOf(PropTypes.object),
    tankGyles: PropTypes.arrayOf(PropTypes.object),
    plannedBrews: PropTypes.arrayOf(PropTypes.object),
    events: PropTypes.arrayOf(PropTypes.object),
    skuLines: PropTypes.arrayOf(PropTypes.object),
    actualMonthly: PropTypes.arrayOf(PropTypes.object),
    monthly: PropTypes.arrayOf(PropTypes.object),
  }),
  order: PropTypes.arrayOf(PropTypes.string).isRequired,
  plan: PropTypes.shape({
    today: PropTypes.string,
    weekStarts: PropTypes.arrayOf(PropTypes.string),
    settings: PropTypes.shape({
      packagingLossPct: PropTypes.number,
      tanks: PropTypes.arrayOf(PropTypes.object),
    }),
    events: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  canEdit: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onStep: PropTypes.func.isRequired,
  onScheduleRecommendation: PropTypes.func.isRequired,
  onEditBrew: PropTypes.func.isRequired,
  onOpenEvent: PropTypes.func.isRequired,
};

BeerDrawer.defaultProps = { beer: null };

export default BeerDrawer;
